import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { formatDate, formatDateShort } from "@/shared/core";
import type { ConsultationWithDetails } from "../types/consultation.types";

interface ConsultationsTableProps {
  consultations: ConsultationWithDetails[];
  onView: (consultationId: string) => void;
  onAccept: (consultationId: string) => void;
  onReject: (consultationId: string) => void;
  loading?: boolean;
}

type SortField =
  | "client"
  | "description"
  | "status"
  | "urgency"
  | "practice_area"
  | "date";
type SortDirection = "asc" | "desc";

export const ConsultationsTable: React.FC<ConsultationsTableProps> = ({
  consultations,
  onView,
  onAccept,
  onReject,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedConsultations = [...consultations].sort((a, b) => {
    let compareA: string | number | Date;
    let compareB: string | number | Date;

    switch (sortField) {
      case "client":
        compareA = a.client_profiles?.profiles?.full_name?.toLowerCase() || "";
        compareB = b.client_profiles?.profiles?.full_name?.toLowerCase() || "";
        break;
      case "description":
        compareA = a.description?.toLowerCase() || "";
        compareB = b.description?.toLowerCase() || "";
        break;
      case "status":
        compareA = a.status;
        compareB = b.status;
        break;
      case "urgency": {
        const urgencyOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
        compareA = urgencyOrder[a.urgency as keyof typeof urgencyOrder] || 0;
        compareB = urgencyOrder[b.urgency as keyof typeof urgencyOrder] || 0;
        break;
      }
      case "practice_area":
        compareA = a.practice_area?.toLowerCase() || "";
        compareB = b.practice_area?.toLowerCase() || "";
        break;
      case "date":
        compareA = new Date(a.created_at);
        compareB = new Date(b.created_at);
        break;
      default:
        return 0;
    }

    if (compareA < compareB) return sortDirection === "asc" ? -1 : 1;
    if (compareA > compareB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-dorado/20 text-dorado border border-dorado/30">
            {t("lawyer.consultations.status.pending")}
          </span>
        );
      case "accepted":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-atlantis-100 text-atlantis-800">
            {t("lawyer.consultations.status.accepted")}
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {t("lawyer.consultations.status.rejected")}
          </span>
        );
      case "completed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.consultations.status.completed")}
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.consultations.status.cancelled")}
          </span>
        );
      default:
        return null;
    }
  };

  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case "urgent":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            {t("lawyer.consultations.urgency.urgent")}
          </span>
        );
      case "high":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
            {t("lawyer.consultations.urgency.high")}
          </span>
        );
      case "normal":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.consultations.urgency.normal")}
          </span>
        );
      case "low":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.consultations.urgency.low")}
          </span>
        );
      default:
        return null;
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-200 p-8 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-tiber-200 rounded w-full mb-4"></div>
          <div className="h-8 bg-tiber-200 rounded w-full mb-4"></div>
          <div className="h-8 bg-tiber-200 rounded w-full mb-4"></div>
        </div>
      </div>
    );
  }

  if (consultations.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-200 p-12 text-center">
        <UserIcon className="h-12 w-12 text-titan mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-juridico mb-2">
          {t("lawyer.consultations.empty.title")}
        </h3>
        <p className="text-tiber-700">
          {t(
            "lawyer.consultations.empty.description",
            "No consultation requests at this time"
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-tiber-200 overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-tiber-200">
          <thead className="bg-romance-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("client")}
              >
                {t("lawyer.consultations.table.client")}
                <SortIcon field="client" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("description")}
              >
                {t("lawyer.consultations.table.description")}
                <SortIcon field="description" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("practice_area")}
              >
                {t("lawyer.consultations.table.practiceArea")}
                <SortIcon field="practice_area" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("urgency")}
              >
                {t("lawyer.consultations.table.urgency")}
                <SortIcon field="urgency" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("status")}
              >
                {t("lawyer.consultations.table.status")}
                <SortIcon field="status" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("date")}
              >
                {t("lawyer.consultations.table.date")}
                <SortIcon field="date" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-titan uppercase tracking-wider"
              >
                {t("lawyer.consultations.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-tiber-200">
            {sortedConsultations.map((consultation, index) => (
              <motion.tr
                key={consultation.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="hover:bg-romance-50 transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="shrink-0 h-10 w-10 rounded-lg bg-linear-to-br from-tiber-500 to-tiber-600 flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-juridico">
                        {consultation.client_profiles?.profiles?.full_name ||
                          "N/A"}
                      </div>
                      {consultation.client_profiles?.profiles?.email && (
                        <div className="text-sm text-titan">
                          {consultation.client_profiles.profiles.email}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-juridico line-clamp-2 max-w-md">
                    {consultation.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-juridico capitalize">
                    {t(
                      `lawyer.consultations.practiceAreas.${consultation.practice_area}`,
                      consultation.practice_area
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getUrgencyBadge(consultation.urgency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(consultation.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-juridico">
                    {formatDateShort(consultation.created_at)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onView(consultation.id)}
                      className="text-tiber-600 hover:text-tiber-900 p-1 rounded hover:bg-tiber-50 transition-colors"
                      title={t("lawyer.consultations.actions.view")}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {consultation.status === "pending" && (
                      <>
                        <button
                          onClick={() => onReject(consultation.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                          title={t(
                            "lawyer.consultations.actions.reject",
                            "Reject"
                          )}
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => onAccept(consultation.id)}
                          className="text-atlantis-600 hover:text-atlantis-900 p-1 rounded hover:bg-atlantis-50 transition-colors"
                          title={t(
                            "lawyer.consultations.actions.accept",
                            "Accept"
                          )}
                        >
                          <CheckIcon className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-tiber-200">
        {sortedConsultations.map((consultation, index) => (
          <motion.div
            key={consultation.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-4 hover:bg-romance-50"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center flex-1 min-w-0">
                <div className="shrink-0 h-12 w-12 rounded-lg bg-linear-to-br from-tiber-500 to-tiber-600 flex items-center justify-center mr-3">
                  <UserIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-juridico truncate">
                    {consultation.client_profiles?.profiles?.full_name || "N/A"}
                  </div>
                  {consultation.client_profiles?.profiles?.email && (
                    <div className="text-xs text-titan truncate">
                      {consultation.client_profiles.profiles.email}
                    </div>
                  )}
                </div>
              </div>
              {getStatusBadge(consultation.status)}
            </div>
            <div className="text-sm text-tiber-700 mb-3 line-clamp-2">
              {consultation.description}
            </div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {getUrgencyBadge(consultation.urgency)}
              <span className="text-xs text-titan capitalize">
                {t(
                  `lawyer.consultations.practiceAreas.${consultation.practice_area}`,
                  consultation.practice_area
                )}
              </span>
              <span className="text-xs text-titan">
                {formatDate(consultation.created_at)}
              </span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => onView(consultation.id)}
                className="flex-1 px-3 py-2 bg-tiber-50 text-tiber-700 rounded-lg hover:bg-tiber-100 transition-colors text-sm font-medium"
              >
                {t("lawyer.consultations.actions.view")}
              </button>
              {consultation.status === "pending" && (
                <>
                  <button
                    onClick={() => onReject(consultation.id)}
                    className="flex-1 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                  >
                    {t("lawyer.consultations.actions.reject")}
                  </button>
                  <button
                    onClick={() => onAccept(consultation.id)}
                    className="flex-1 px-3 py-2 bg-atlantis-50 text-atlantis-700 rounded-lg hover:bg-atlantis-100 transition-colors text-sm font-medium"
                  >
                    {t("lawyer.consultations.actions.accept")}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
